import styled, { CSSObject } from '@emotion/styled'
import IconTooltip from 'shop/components/Tooltip/Tooltip'
import ContentLoader from 'react-content-loader'
import { IconType } from 'react-icons'
import { StyledHTMLElement } from 'shop/theme/types'
import { LineBreak } from 'shop/components/Shop/commonStyles'
import { CustomIconType } from 'shop/assets/icons'

interface Props {
  RightIcon?: IconType | CustomIconType
  LeftIcon?: IconType | CustomIconType
  withTooltip?: boolean
  withEllipsis?: boolean
  toolTipMessage?: string
  fontWeight?: number
  padding?: string
  fontSize?: number
  maxWidth?: string
  label: string | React.ReactNode
  isLoading?: boolean
  children?: React.ReactNode
  withLineBreak?: boolean
  lineBreakHeight?: string
  onChangeButtonClick?: () => void
  changeButtonText?: string
}

const GenericBreakdownItem = ({
  maxWidth = '70%',
  label,
  padding = '',
  withEllipsis = true,
  withTooltip,
  toolTipMessage,
  LeftIcon,
  RightIcon,
  children,
  withLineBreak = true,
  onChangeButtonClick,
  isLoading = false,
  lineBreakHeight = '1px',
  changeButtonText = 'Change'
}: Props) => {
  return (
    <div data-testid='generic-breakdown-item'>
      {withLineBreak && (
        <LineBreak
          height={lineBreakHeight}
          data-testid='generic-breakdown-line'
        />
      )}
      <Content
        data-testid='generic-breakdown-content'
        hasChildren={!!children}
        maxWidth={maxWidth}
        padding={padding}
      >
        <>
          <Item>
            {!!LeftIcon && <LeftIcon data-testid='left-icon' />}
            <Label withEllipsis={withEllipsis}>
              <>{label}</>
            </Label>
            {!!withTooltip && toolTipMessage && (
              <IconTooltip
                tooltipMessage={toolTipMessage}
                tooltipHeader={typeof label === 'string' ? label : undefined}
              />
            )}
            {!!RightIcon && <RightIcon data-testid='right-icon' />}
            {!!onChangeButtonClick && !isLoading && (
              <ChangeText
                data-testid='change-button'
                onClick={onChangeButtonClick}
              >
                {changeButtonText}
              </ChangeText>
            )}
          </Item>
        </>
        {isLoading && (
          <ContentLoader
            data-testid='generic-breakdown-price-loader'
            speed={2}
            style={{
              width: '55px',
              height: '18px',
              borderRadius: '5px',
              alignSelf: 'center'
            }}
          />
        )}
      </Content>
      {!!children && children}
    </div>
  )
}

const Content = styled.div<
  StyledHTMLElement & {
    hasChildren: boolean
    maxWidth: string
    padding: string
  }
>(
  ({ hasChildren, maxWidth, padding, theme }): CSSObject => ({
    '> div': {
      maxWidth: maxWidth
    },
    padding: !!padding ? padding : hasChildren ? '8px 0 0' : '8px 0',
    display: 'flex',
    justifyContent: 'space-between'
  })
)

const Item = styled.div(() => ({
  alignItems: 'center',
  display: 'flex',
  flex: 'auto',
  '> svg': {
    marginRight: '8px',
    width: '20px',
    height: '20px',
    flexShrink: 0
  },
  'div > svg': {
    marginLeft: '4px',
    width: '12px',
    height: '12px',
    fill: '#59595A',
    flexShrink: 0
  }
}))

const ChangeText = styled.p<StyledHTMLElement>(
  (): CSSObject => ({
    fontSize: '14px',
    margin: '0 0 0 auto',
    paddingLeft: '8px',
    cursor: 'pointer',
    alignSelf: 'flex-start',
    '&:hover': {
      opacity: 0.8
    }
  })
)

const Label = styled.span<
  StyledHTMLElement & { withEllipsis: boolean; fontWeight?: number }
>(({ withEllipsis, fontWeight = 400 }) => ({
  margin: 0,
  ...(withEllipsis && {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  }),
  fontWeight: fontWeight
}))

export default GenericBreakdownItem
